import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

const DEFAULT_ORDER = { column: 'created_at', ascending: false };

export const useSupabaseResource = (
  table,
  { select = '*', order = DEFAULT_ORDER.column, ascending = DEFAULT_ORDER.ascending } = {}
) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    const query = supabase.from(table).select(select);

    if (order) {
      query.order(order, { ascending });
    }

    const { data, error: supabaseError } = await query;
    if (supabaseError) {
      setError(supabaseError.message);
      setRecords([]);
    } else {
      setRecords(Array.isArray(data) ? data : data ? [data] : []);
    }
    setLoading(false);
  }, [table, select, order, ascending]);

  const upsertRecord = useCallback(
    async (payload) => {
      // For new records (no valid id), remove the id field to let Supabase generate UUID
      // For updates, keep the id field
      const upsertPayload = { ...payload };
      if (!upsertPayload.id) {
        delete upsertPayload.id;
      }

      const { data, error: supabaseError } = await supabase
        .from(table)
        .upsert(upsertPayload)
        .select();

      if (supabaseError) {
        throw new Error(supabaseError.message);
      }

      const record = Array.isArray(data) ? data[0] : data;
      setRecords((prev) => {
        const index = prev.findIndex((item) => item.id === record.id);
        if (index >= 0) {
          const clone = [...prev];
          clone[index] = record;
          return clone;
        }
        return [record, ...prev];
      });

      // Notify other parts of the app that a resource changed
      try {
        window.dispatchEvent(new CustomEvent('resourceUpdated', { detail: { table, record } }));
      } catch (e) {
        // ignore
      }

      return record;
    },
    [table]
  );

  const deleteRecord = useCallback(
    async (id) => {
      const { error: supabaseError } = await supabase.from(table).delete().eq('id', id);
      if (supabaseError) {
        throw new Error(supabaseError.message);
      }
      setRecords((prev) => prev.filter((item) => item.id !== id));
      try {
        window.dispatchEvent(new CustomEvent('resourceDeleted', { detail: { table, id } }));
      } catch (e) {
        // ignore
      }
    },
    [table]
  );

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  return useMemo(
    () => ({
      records,
      loading,
      error,
      refresh: fetchRecords,
      upsertRecord,
      deleteRecord,
    }),
    [records, loading, error, fetchRecords, upsertRecord, deleteRecord]
  );
};
